import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import api from "../../utils/axios";
import useLang from "../../hooks/useLang";
import useCurrencies, { reloadCurrencyList } from "../../hooks/useCurrencies";

type Props = {};

const CurrencyManagement = (props: Props) => {
	type Currency = {
		id: number;
		description: string;
		/** Buy rate — kursi i blerjes. Converts OUT of EUR, by division. */
		rate: number;
		/** Sell rate — kursi i shitjes. Converts INTO EUR, by multiplication. */
		sellCurrencyRate: number;
	};
	const [currencies, setCurrencies] = useState<Currency[]>([]);
	const { dualCurrencyEnabled } = useCurrencies();
	const defaultColDef = {
		sortable: true,
		filter: true,
		wrapHeaderText: true,
		resizable: true,
		filterParams: { excelMode: "windows" },
		autoSizeAllColumns: true,
	};
	const { strings } = useLang("currency");

	// Rates are stored to 6 decimals and must NOT be rounded — 0.010410 and 0.010980 differ in the
	// fifth place, and rendering them at 2 would show both as 0.01. AG-Grid's default renderer
	// would also drop the trailing zero, so the value is formatted explicitly.
	const rateFormatter = (params: any) =>
		params.value === null || params.value === undefined
			? ""
			: Number(params.value).toFixed(6);

	const tableColumns = useMemo(() => {
		const columns: any[] = [
			{
				field: "id",
				headerName: strings?.id,
				type: "numericColumn",
				filter: "agNumberColumnFilter",
			},
			{
				field: "description",
				headerName: strings?.description,
				editable: false,
			},
			{
				field: "rate",
				// Where there is only one currency this is just "the rate"; where there are two it
				// has to say which one, because the sell rate sits next to it.
				headerName: dualCurrencyEnabled
					? strings?.buyRate
					: strings?.value,
				editable: true,
				valueFormatter: rateFormatter,
			},
		];

		// Single-currency deployments have nothing to quote a spread against — both columns hold
		// the same number and the API strips the field on update, so the column would be inert.
		if (dualCurrencyEnabled) {
			columns.push({
				field: "sellCurrencyRate",
				headerName: strings?.sellRate,
				editable: true,
				valueFormatter: rateFormatter,
			});
		}

		return columns;
	}, [strings, dualCurrencyEnabled]);
	useEffect(() => {
		(async () => await fetchCurrencies())();
	}, []);

	const fetchCurrencies = async () => {
		try {
			const response = await api.get<Currency[]>("Currency");
			if (response.status === 200) {
				setCurrencies(response.data);
			}
		} catch (error) {
			console.error("Error fetching Currencies:", error);
		}

		return [];
	};

	const updateCurrency = async (
		id: number,
		rate: number,
		sellCurrencyRate?: number
	) => {
		try {
			// sellCurrencyRate is omitted where dual currency is off. The API ignores it there
			// anyway, but sending only what the deployment understands keeps the request identical
			// to what it has always been on Kosovo and Macedonia.
			const response = await api.post("Currency/update", {
				id,
				rate,
				...(dualCurrencyEnabled ? { sellCurrencyRate } : {}),
			});
			if (response.status === 204) {
				reloadCurrencyList();
				fetchCurrencies();
			}
		} catch (error) {
			console.error("Error updating Currency:", error);
		}
	};
	const onRowEdit = async (row: any) => {
		// editType is fullRow, so both rates commit together and either may have changed.
		await updateCurrency(
			row.data.id,
			Number(row.data.rate),
			row.data.sellCurrencyRate === undefined ||
				row.data.sellCurrencyRate === null
				? undefined
				: Number(row.data.sellCurrencyRate)
		);
		await fetchCurrencies();
	};
	return (
		<div>
			<div className="ag-theme-alpine" style={{ height: 600 }}>
				<AgGridReact
					rowData={currencies}
					defaultColDef={defaultColDef}
					columnDefs={tableColumns}
					enterMovesDown={true}
					enterMovesDownAfterEdit={true}
					undoRedoCellEditing={true}
					undoRedoCellEditingLimit={20}
					suppressCopyRowsToClipboard={true}
					rowSelection="single"
					enableRangeSelection={true}
					editType={"fullRow"}
					onRowValueChanged={(event) => {
						onRowEdit(event);
					}}
				/>
			</div>
		</div>
	);
};

export default CurrencyManagement;
